import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  TrendingDown,
  Target,
  Sparkles,
  Eye,
  ArrowRight,
  DollarSign,
  Zap,
} from 'lucide-react';
import { clsx } from 'clsx';
import {
  creativeWorkbenchApi,
  type WorkbenchKpiResponse,
  type WorkbenchKpiTile,
} from '@/services/api';
import { useCreativeWorkbench } from '../CreativeWorkbenchContext';

interface Props {
  onTileClick?: (tile: WorkbenchKpiTile) => void;
}

const ICONS: Record<string, JSX.Element> = {
  aligned_spend_pct: <Sparkles size={14} />,
  steering_accuracy: <Target size={14} />,
  aligned_roas_lift: <TrendingUp size={14} />,
  ltv_weighted_roas: <TrendingUp size={14} />,
  reviewed_coverage: <Eye size={14} />,
  captured_incremental_revenue: <DollarSign size={14} />,
  revenue_upside: <Zap size={14} />,
};

// Tiles that use a distinct teal highlight (revenue signal tiles)
const REVENUE_TILE_KEYS = new Set(['captured_incremental_revenue', 'revenue_upside']);

export function KpiStrip({ onTileClick }: Props) {
  const navigate = useNavigate();
  const { clientUid, personaId, dateRange } = useCreativeWorkbench();
  const [data, setData] = useState<WorkbenchKpiResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!clientUid) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    creativeWorkbenchApi
      .getPerformanceKpis({
        clientUid,
        dateFrom: dateRange.from,
        dateTo: dateRange.to,
        personaId: personaId ?? null,
      })
      .then((p) => {
        if (!cancelled) setData(p);
      })
      .catch((err) => {
        if (!cancelled) {
          setData(null);
          const msg = err?.response?.data?.detail ?? err?.message ?? 'Analytics unavailable';
          setError(String(msg));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientUid, personaId, dateRange.from, dateRange.to]);

  const handleTile = (tile: WorkbenchKpiTile) => {
    if (onTileClick) {
      onTileClick(tile);
      return;
    }
    if (tile.drill_down === 'steering') {
      navigate('/creative/performance/steering');
    } else if (tile.drill_down === 'coverage') {
      navigate('/creative/performance/coverage');
    }
  };

  const tiles = data?.tiles ?? PLACEHOLDER_TILES;
  const diagnosticTiles = tiles.filter((t) => !REVENUE_TILE_KEYS.has(t.key));
  const revenueTiles = tiles.filter((t) => REVENUE_TILE_KEYS.has(t.key));

  return (
    <div className="space-y-2">
      {error && !loading && (
        <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-2 text-sm text-amber-800">
          <span className="font-medium">Analytics unavailable:</span>{' '}
          {error.length > 200 ? error.slice(0, 200) + '…' : error}
        </div>
      )}
      {/* Row 1 — diagnostic KPIs (5 tiles) */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {diagnosticTiles.map((tile) => (
          <KpiTile
            key={tile.key}
            tile={tile}
            loading={loading && !data}
            onClick={() => handleTile(tile)}
          />
        ))}
      </div>

      {/* Row 2 — revenue intelligence tiles (shown only when data loaded) */}
      {revenueTiles.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {revenueTiles.map((tile) => (
            <KpiTile
              key={tile.key}
              tile={tile}
              loading={loading && !data}
              onClick={() => handleTile(tile)}
              highlight
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DeltaArrow({ value }: { value: number }) {
  if (value === 0) return null;
  const Icon = value > 0 ? TrendingUp : TrendingDown;
  const color = value > 0 ? 'text-green-500' : 'text-red-500';
  return <Icon size={14} className={color} />;
}

function formatValue(value: number, format: string): string {
  if (Number.isNaN(value)) return '—';
  if (format === 'percent') return `${value.toFixed(1)}%`;
  if (format === 'ratio') return `${value.toFixed(2)}x`;
  if (format === 'ratio_delta') {
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}x`;
  }
  if (format === 'currency') {
    // Compact format for large values: $1.3k, $5.1k, etc.
    if (Math.abs(value) >= 1000) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(value);
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value);
  }
  return value.toLocaleString();
}

interface KpiTileProps {
  tile: WorkbenchKpiTile;
  loading: boolean;
  onClick: () => void;
  highlight?: boolean;
}

function KpiTile({ tile, loading, onClick, highlight = false }: KpiTileProps) {
  const isRevenue = REVENUE_TILE_KEYS.has(tile.key);
  return (
    <button
      onClick={onClick}
      className={clsx(
        'group border rounded-xl p-3 text-left transition-all',
        highlight
          ? 'bg-gradient-to-br from-teal-50/60 to-white border-teal-200 hover:border-teal-400 hover:shadow-sm'
          : 'bg-white border-slate-200 hover:border-teal-400 hover:shadow-sm',
      )}
    >
      <div className="flex items-center justify-between mb-2">
        <span
          className={clsx(
            'text-[10px] font-mono uppercase tracking-wide',
            isRevenue ? 'text-teal-600' : 'text-slate-500',
          )}
        >
          {tile.label}
        </span>
        <span className={clsx('group-hover:text-teal-500 transition-colors', isRevenue ? 'text-teal-400' : 'text-slate-400')}>
          {ICONS[tile.key] ?? <Sparkles size={14} />}
        </span>
      </div>
      <div className="flex items-baseline gap-1">
        <span
          className={clsx(
            'font-bold tabular-nums',
            isRevenue ? 'text-3xl text-teal-700' : 'text-2xl text-navy-900',
          )}
        >
          {loading ? '—' : formatValue(tile.value, tile.format)}
        </span>
        {tile.format === 'ratio_delta' && <DeltaArrow value={tile.value} />}
      </div>
      <p className="text-[10px] text-slate-500 mt-1 line-clamp-2" title={tile.subtitle}>
        {tile.subtitle}
      </p>
      <div className="flex items-center gap-1 text-[10px] text-teal-600 opacity-0 group-hover:opacity-100 transition-opacity mt-2">
        Drill into {tile.drill_down}
        <ArrowRight size={10} />
      </div>
    </button>
  );
}

const PLACEHOLDER_TILES: WorkbenchKpiTile[] = [
  {
    key: 'aligned_spend_pct',
    label: 'Aligned Spend',
    value: 0,
    format: 'percent',
    subtitle: 'Loading...',
    drill_down: 'scatter',
  },
  {
    key: 'steering_accuracy',
    label: 'Persona-Steering Accuracy',
    value: 0,
    format: 'percent',
    subtitle: 'Loading...',
    drill_down: 'steering',
  },
  {
    key: 'aligned_roas_lift',
    label: 'Aligned ROAS Lift',
    value: 0,
    format: 'ratio_delta',
    subtitle: 'Loading...',
    drill_down: 'scatter',
  },
  {
    key: 'ltv_weighted_roas',
    label: 'LTV-Weighted ROAS',
    value: 0,
    format: 'ratio',
    subtitle: 'Loading...',
    drill_down: 'leaderboard',
  },
  {
    key: 'reviewed_coverage',
    label: 'Reviewed Coverage',
    value: 0,
    format: 'percent',
    subtitle: 'Loading...',
    drill_down: 'coverage',
  },
  {
    key: 'captured_incremental_revenue',
    label: 'Alignment Revenue Captured',
    value: 0,
    format: 'currency',
    subtitle: 'Loading...',
    drill_down: 'scatter',
  },
  {
    key: 'revenue_upside',
    label: 'Revenue Upside',
    value: 0,
    format: 'currency',
    subtitle: 'Loading...',
    drill_down: 'coverage',
  },
];
